import React from "react";
import { Modal } from "../ui/modal";
import { ReservaB2C, ReservaB2B } from "../../hooks/useApi";
import { useNavigate } from "react-router";
import Button from "../ui/button/Button";

interface TourReservationsListModalProps {
  isOpen: boolean;
  onClose: () => void;
  tourName: string;
  tourDate: string;
  reservations: Array<any>; // Changed to any[] to handle all possible input formats
  onSelectReservation: (reservation: ReservaB2C | ReservaB2B) => void;
}

const TourReservationsListModal: React.FC<TourReservationsListModalProps> = ({
  isOpen,
  onClose,
  tourName,
  tourDate,
  reservations,
  onSelectReservation
}) => {
  const navigate = useNavigate();

  // Helper function to get status display
  const getStatusDisplay = (status: string): { text: string; bgClass: string } => {
    switch (status) {
      case "pending_payment":
        return { text: "Pago pendiente", bgClass: "bg-yellow-100 text-yellow-800" };
      case "partial_payment":
        return { text: "Pago parcial", bgClass: "bg-blue-100 text-blue-800" };
      case "paid":
        return { text: "Pagada", bgClass: "bg-green-100 text-green-800" };
      case "no_show":
        return { text: "No show", bgClass: "bg-red-100 text-red-800" };
      case "completed":
        return { text: "Completada", bgClass: "bg-gray-100 text-gray-800" };
      default:
        return { text: status, bgClass: "bg-gray-100 text-gray-800" };
    }
  };

  // Transform the reservation before sending to the parent component
  const handleSelectReservation = (reservation: any) => {
    // Check if the reservation has all the necessary properties for B2C/B2B
    // If not, transform it to the expected format
    let transformedReservation: ReservaB2C | ReservaB2B;
    
    // Helper to safely extract names from client data
    const getNameFromClient = (client: any) => {
      if (!client) return "";
      if (typeof client === 'string') return client;
      // If client is an object with a name property, use that
      if (typeof client === 'object' && client.name) return client.name;
      // Fallback to empty string to avoid rendering objects
      return "";
    };
    
    // Determine if this is B2C or B2B based on the data
    const isB2C = typeof reservation.client === 'string' && !reservation.client.includes('TOURS');
    
    if (isB2C) {
      // Transform to ReservaB2C
      transformedReservation = {
        ...reservation,
        type: "b2c",
        clienteName: getNameFromClient(reservation.client),
        excursion: reservation.tour || reservation.excursion,
        fecha: reservation.date || reservation.fecha,
        estado: reservation.status || reservation.estado,
        // Ensure it has all the necessary properties from the ReservaB2C interface
        adultos: reservation.adultos || 0,
        ninios: reservation.ninios || 0,
        hotel: reservation.hotel || "",
        precioTotal: reservation.precioTotal || 0,
        montoFacturado: reservation.montoFacturado || 0,
        saldoPendiente: reservation.saldoPendiente || 0
      } as ReservaB2C;
    } else {
      // Transform to ReservaB2B
      transformedReservation = {
        ...reservation,
        type: "b2b",
        agenciaName: getNameFromClient(reservation.client),
        excursion: reservation.tour || reservation.excursion,
        fecha: reservation.date || reservation.fecha,
        estado: reservation.status || reservation.estado,
        // Ensure it has all the necessary properties from the ReservaB2B interface
        adultos: reservation.adultos || 0,
        ninios: reservation.ninios || 0,
        hotel: reservation.hotel || "",
        precioTotal: reservation.precioTotal || 0,
        montoFacturado: reservation.montoFacturado || 0,
        saldoPendiente: reservation.saldoPendiente || 0,
        // Include client end name if available
        clientEndName: getNameFromClient(reservation.clientEndName)
      } as ReservaB2B;
    }

    onSelectReservation(transformedReservation);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Reservas para ${tourName}`}
      size="lg"
      className="max-w-4xl"
    >
      <div className="p-4 space-y-6">
        {/* Tour information header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{tourName}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Fecha: {tourDate}</p>
          </div>
          <div className="mt-2 md:mt-0">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
              {reservations.length} {reservations.length === 1 ? "reserva" : "reservas"}
            </span>
          </div>
        </div>

        {/* List of reservations */}
        {reservations.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-500 dark:text-gray-400">No hay reservas disponibles para este tour</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-auto">
            {reservations.map((reservation) => {
              const { text, bgClass } = getStatusDisplay(reservation.status || reservation.estado);
              
              // Use the type property if it exists and is b2c/b2b, otherwise determine from the client name
              const isB2C = reservation.type === "b2c" || 
                            (typeof reservation.client === 'string' && !reservation.client.includes('TOURS'));
              
              // Helper to safely extract names from client data
              const getNameFromClient = (client: any) => {
                if (!client) return "";
                if (typeof client === 'string') return client;
                // If client is an object with a name property, use that
                if (typeof client === 'object' && client.name) return client.name;
                // Fallback to empty string to avoid rendering objects
                return "";
              };

              // Get the client/agency name and client end name
              const displayName = isB2C 
                ? (reservation.clienteName || getNameFromClient(reservation.client) || "Cliente sin nombre")
                : (reservation.agenciaName || getNameFromClient(reservation.client) || "Agencia sin nombre");
              
              // Get client end name for B2B
              const clientEndName = !isB2C ? getNameFromClient(reservation.clientEndName) : null;
              
              return (
                <div
                  key={reservation.id}
                  className="flex flex-col md:flex-row justify-between items-start md:items-center p-4 bg-white dark:bg-gray-800/70 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                  onClick={() => handleSelectReservation(reservation)}
                >
                  {/* Main content */}
                  <div className="w-full md:w-auto">
                    <div className="flex items-center">
                      <span className="text-base font-medium text-gray-900 dark:text-white">
                        #{reservation.id} - {displayName}
                      </span>
                      <span className={`ml-2 inline-block px-2 py-1 rounded-full text-xs font-semibold ${bgClass}`}>
                        {text}
                      </span>
                    </div>
                    
                    {/* Additional client info for B2B */}
                    {!isB2C && clientEndName && (
                      <div className="mt-1 text-sm text-blue-600 dark:text-blue-400 font-medium">
                        Cliente final: {clientEndName}
                      </div>
                    )}
                    
                    <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center">
                        <span className="font-medium">Tipo:</span>
                        <span className="ml-1">{isB2C ? "Cliente B2C" : "Agencia B2B"}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium">Pax:</span>
                        <span className="ml-1">
                          {(reservation.adultos + reservation.ninios) || reservation.personas || 0} 
                          {reservation.adultos !== undefined && (
                            ` (${reservation.adultos} adultos, ${reservation.ninios} niños)`
                          )}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium">Total:</span>
                        <span className="ml-1">${(reservation.precioTotal || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* View details button (visible only on mobile) */}
                  <div className="mt-3 md:mt-0 w-full md:w-auto flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      className="md:hidden w-full"
                      onClick={(e?: React.MouseEvent<Element, MouseEvent>) => {
                        if (e) e.stopPropagation();
                        handleSelectReservation(reservation);
                      }}
                    >
                      Ver detalles
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer with actions */}
        <div className="flex justify-end items-center pt-4 border-t border-gray-200 dark:border-gray-700 space-x-2">
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
          <Button 
            variant="primary"
            onClick={() => {
              navigate(`/reservas?excursion=${encodeURIComponent(tourName)}&fecha=${encodeURIComponent(tourDate)}`);
              onClose();
            }}
          >
            Ver en reservas
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TourReservationsListModal; 